import React, { useState, useEffect, useCallback } from 'react';
import { TextField, IconButton } from '@material-ui/core';
import useDebounce from '../../Util/useDebounce';
import VideoStatus, { TVideoInfo } from './VideoStatus';
import styled from 'styled-components';
import { Video } from '../../VideoPlayer/VideoTypes';
import sessionsService from '../../../services/sessions';
import { secondsToTimeSpan } from '../../../services/sessionModels';
import useSession, { refreshSession } from '../../Util/useSession';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import AddIcon from '@material-ui/icons/Add';

interface Props {
  video?: Video;
  label: string;
  sortOrder?: number;
  sessionid: string;
  baseFps?: number;
}

const SessionVideoEnterUrl: React.FunctionComponent<Props> = ({ video, label, sessionid, sortOrder, baseFps }) => {
  const [val, setVal] = useState(video?.url || '');
  const debouncedUrl = useDebounce(val, 1000);
  const { session } = useSession(sessionid);
  const [videoInfo, setVideoInfo] = useState<TVideoInfo>();

  useEffect(() => {
    if (video?.url) setVal(video?.url);
  }, [video]);

  const handleChange = useCallback((evt) => {
    setVal(evt.target.value);
  }, []);

  const isOk = useCallback(
    (videoInfo: TVideoInfo) => {
      if (session && !session.videos?.find((v) => v.url === videoInfo.url)) {
        setVideoInfo(videoInfo);
      }
    },
    [session]
  );

  const addVideoCB = useCallback(() => {
    if (videoInfo) {
      sessionsService
        .addVideo(sessionid, { url: videoInfo.url, sortOrder: sortOrder || 0, syncOffset: secondsToTimeSpan(0) })
        .then(() => refreshSession(sessionid))
        .then(() => {
          setVal('');
          setVideoInfo(undefined);
        });
    }
  }, [sessionid, videoInfo, sortOrder]);

  const deleteVideo = useCallback(
    (video: Video) => {
      sessionsService.deleteVideo(video.id).then(() => refreshSession(sessionid));
    },
    [sessionid]
  );

  return (
    <Container>
      <TextContainer>
        <TextField fullWidth label={label} name={label} value={val} onChange={handleChange} />
        <StatusContainer>
          <VideoStatus key={debouncedUrl} url={debouncedUrl} ok={isOk} baseFps={baseFps} />
          {video ? (
            <IconButton onClick={() => deleteVideo(video)}>
              <HighlightOffIcon color="primary" />
            </IconButton>
          ) : (
            <IconButton disabled={!videoInfo} onClick={addVideoCB} color="primary">
              <AddIcon />
            </IconButton>
          )}
        </StatusContainer>
      </TextContainer>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 20px;
  flex-grow: 1;
`;

const StatusContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: baseline;
`;
export default SessionVideoEnterUrl;
